import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { AlertCircle, CalendarClock, Check, ChevronDown, History, Layers, Loader2, Rocket, Search, Sparkles, Wrench, X } from 'lucide-react';
import { API_BASE_URL } from '../../apiBase';
import { useLanguage } from '../../context/LanguageContext';
import { parseReleaseForDisplay, resolveReleaseBody } from '../../lib/releaseNotesFormat';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogFooter } from '../ui/dialog';
import OfficeModalHeader from './OfficeModalHeader';
import './ReleaseHistoryModal.css';

const SECTION_STYLE = {
  '✨': { icon: Sparkles, tone: 'blue' }, '🚀': { icon: Rocket, tone: 'green' },
  '🛠️': { icon: Wrench, tone: 'amber' }, '⚙️': { icon: Layers, tone: 'violet' },
  '📌': { icon: History, tone: 'cyan' },
};
const searchText = text => String(text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/** Datas sem offset vêm do Mongo como UTC; exibição no fuso do dispositivo. */
export function releaseDate(iso) {
  if (!iso || !String(iso).trim()) return null;
  const raw = String(iso).trim();
  const date = new Date(/[zZ]$|[+-]\d{2}:?\d{2}$/.test(raw) ? raw : raw.replace(/\s+/, 'T') + 'Z');
  return Number.isNaN(date.getTime()) ? null : date;
}

function Category({ section, initiallyOpen }) {
  const [expanded, setExpanded] = useState(initiallyOpen);
  const id = useId();
  const { icon: Icon, tone } = SECTION_STYLE[section.emoji] || SECTION_STYLE['📌'];
  return <section className="rh-category" data-tone={tone}>
    <h4><button type="button" className="rh-category-toggle" aria-expanded={expanded} aria-controls={id} onClick={() => setExpanded(value => !value)}>
      <span className="rh-icon" aria-hidden="true"><Icon size={16} strokeWidth={1.8} /></span>
      <span className="rh-category-label">{section.title}</span>
      <span className="rh-count" aria-label={section.items.length + (section.items.length === 1 ? ' item' : ' itens')}>{section.items.length}</span>
      <ChevronDown className="rh-chevron" size={16} aria-hidden="true" />
    </button></h4>
    <div id={id} hidden={!expanded}><ul className="rh-changes">{section.items.map((item, index) => <li key={index}>{item}</li>)}</ul></div>
  </section>;
}

function ReleaseEntry({ release, latest, expanded, onToggle, locale, query }) {
  const id = useId();
  const date = releaseDate(release.published_at || release.updated_at);
  const when = date ? new Intl.DateTimeFormat(locale || 'pt-BR', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(date) : 'Data não informada';
  const version = release.version || 'Versão sem identificação';
  const raw = resolveReleaseBody(release, locale) || '';
  const { intro, sections } = useMemo(() => parseReleaseForDisplay(raw, locale), [raw, locale]);
  const customIntro = intro && !/^(Confira o que mudou|Here's what changed|Consulta lo que cambió)/i.test(intro);
  // Keep meaningful titles, including those starting with "Office".
  const title = release.title && ![String(version), 'Office ' + version].includes(String(release.title).trim()) ? release.title : '';
  return <article className={'rh-release' + (latest ? ' rh-release--latest' : '')}>
    <h3><button type="button" className="rh-release-toggle" onClick={onToggle} aria-expanded={expanded} aria-controls={id}>
      <span className="rh-release-copy">
        <span className="rh-version-row"><span className="rh-version">{version}</span>{latest && <span className="rh-latest"><Check size={12} aria-hidden="true" /> Mais recente</span>}</span>
        {title && <span className="rh-release-title">{title}</span>}
        <span className="rh-date"><CalendarClock size={13} aria-hidden="true" />{date ? <time dateTime={date.toISOString()}>{when}</time> : when}</span>
      </span><ChevronDown size={18} className="rh-chevron" aria-hidden="true" />
    </button></h3>
    <div id={id} className="rh-release-body" hidden={!expanded}>
      {expanded && (raw.trim() ? sections.length ? <>
        {customIntro && <p className="rh-intro">{intro}</p>}
        <div className="rh-categories">{sections.map((section, index) => {
          const terms = searchText(query).trim().split(/\s+/).filter(Boolean);
          const matches = terms.length > 0 && terms.every(term => searchText([section.title, ...section.items].join(' ')).includes(term));
          return <Category key={index + '-' + query} section={section} initiallyOpen={index === 0 || matches} />;
        })}</div>
      </> : <p className="rh-plain">{raw}</p> : <p className="rh-intro">Esta versão não possui notas de atualização.</p>)}
    </div>
  </article>;
}

export default function ReleaseHistoryModal({ open, onOpenChange }) {
  const { t, locale } = useLanguage();
  const [loading, setLoading] = useState(false), [error, setError] = useState(false);
  const [releases, setReleases] = useState([]), [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(0);
  const request = useRef(0), searchRef = useRef(null), scrollRef = useRef(null), titleRef = useRef(null);
  const searchId = useId();
  const load = useCallback(async () => {
    const token = ++request.current;
    setLoading(true); setError(false); setReleases([]); setExpanded(0);
    try {
      const { data } = await axios.get(API_BASE_URL + '/api/app/release-history', { params: { limit: 40 }, withCredentials: true });
      if (request.current === token) setReleases(Array.isArray(data?.releases) ? data.releases.filter(item => item && typeof item === 'object') : []);
    } catch {
      if (request.current === token) setError(true);
    } finally {
      if (request.current === token) setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (open) { setQuery(''); load(); }
    return () => { request.current += 1; };
  }, [open, load]);
  const visible = useMemo(() => {
    const terms = searchText(query).trim().split(/\s+/).filter(Boolean);
    return releases.map((release, index) => ({ release, index })).filter(({ release }) => {
      const text = searchText([release.version, release.title, resolveReleaseBody(release, locale)].join(' '));
      return terms.every(term => text.includes(term));
    });
  }, [releases, query, locale]);
  const active = expanded === 'first-match' ? visible[0]?.index : expanded;
  const changeQuery = value => {
    setQuery(value); setExpanded('first-match');
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };
  const clearSearch = () => { changeQuery(''); searchRef.current?.focus(); };
  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="office-release-history" onOpenAutoFocus={event => { event.preventDefault(); titleRef.current?.focus(); }}>
      <OfficeModalHeader layout="row" variant="square" icon={History} title={t('releaseHistory.title')}
        description="Veja as novidades, melhorias e correções do Office." className="rh-header">
        <span ref={titleRef} tabIndex={-1} className="sr-only">Histórico de versões publicadas</span>
      </OfficeModalHeader>
      {!loading && !error && releases.length > 0 && <div className="rh-search-bar">
        <label className="sr-only" htmlFor={searchId}>Buscar versão ou assunto</label>
        <div className="rh-search"><Search size={17} aria-hidden="true" /><input id={searchId} ref={searchRef} type="search" value={query} placeholder="Versão ou assunto..." onChange={event => changeQuery(event.target.value)} />
          {query && <button type="button" aria-label="Limpar busca" onClick={clearSearch}><X size={16} aria-hidden="true" /></button>}
        </div>
        <p className="rh-search-hint" role="status">{query.trim() ? visible.length + (visible.length === 1 ? ' versão encontrada' : ' versões encontradas') : 'Abra uma categoria para ver os detalhes.'}</p>
      </div>}
      <div className="rh-scroll" ref={scrollRef} aria-busy={loading}>
        {loading && <div className="rh-state" role="status"><Loader2 className="animate-spin" size={28} aria-hidden="true" /><p>{t('releaseHistory.loading')}</p></div>}
        {!loading && error && <div className="rh-state"><AlertCircle size={28} aria-hidden="true" /><p role="alert">{t('releaseHistory.loadError')}</p><Button type="button" variant="outline" onClick={load}>{t('releaseHistory.retry')}</Button></div>}
        {!loading && !error && !releases.length && <div className="rh-state"><History size={28} aria-hidden="true" /><p>Ainda não há versões registradas neste Office.</p></div>}
        {!loading && !error && releases.length > 0 && !visible.length && <div className="rh-state"><Search size={28} aria-hidden="true" /><p>Nenhuma versão encontrada.</p><span>Tente outro número de versão ou assunto.</span><Button type="button" variant="outline" onClick={clearSearch}>Limpar busca</Button></div>}
        {!loading && !error && visible.length > 0 && <div className="rh-list">
          {visible.map(({ release, index }) => <ReleaseEntry key={(release.id || release.version) + '-' + index} release={release} latest={index === 0} expanded={active === index} onToggle={() => setExpanded(active === index ? null : index)} locale={locale} query={query} />)}
        </div>}
      </div>
      <DialogFooter className="rh-footer"><span><CalendarClock size={14} aria-hidden="true" /> Horários no seu fuso local</span><Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('releaseHistory.close')}</Button></DialogFooter>
    </DialogContent>
  </Dialog>;
}
